import { caseStudy } from '../data/rwaCaseStudy';
import './Oct2026CaseStudy.css';

const KIND_LABELS = {
  analysis: 'Analysis',
  observed: 'Public evidence',
  method: 'Method & scope',
};

const FLOW_STEPS = [
  { title: 'Underlying asset', question: 'What economic and legal claim does the token represent?' },
  { title: 'Issuance', question: 'Who issues the claim, holds the assets and operates redemption?' },
  { title: 'Distribution', question: 'Who can acquire, transfer and use the token?' },
  { title: 'Hedge', question: 'Where, when and at what cost can inventory risk be offset?' },
];

function displayDate(value) {
  if (!value) return 'Not specified';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

function SourceLinks({ sourceIds = [] }) {
  const sources = sourceIds
    .map(id => caseStudy.sources.find(source => source.id === id))
    .filter(Boolean);

  if (!sources.length) return null;

  return (
    <span className="rwa-source-links">
      <span className="rwa-source-links-label">Sources</span>
      {sources.map(source => (
        <a
          key={source.id}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Read ${source.title} (opens in a new tab)`}
          title={source.title}
        >
          [{caseStudy.sources.findIndex(item => item.id === source.id) + 1}]<span aria-hidden="true"> ↗</span>
        </a>
      ))}
    </span>
  );
}

export default function Oct2026CaseStudy() {
  const { title, deck, asOf, evidenceCutoff, keyTakeaways, products, sections, sources } = caseStudy;

  return (
    <article className="rwa-page" aria-labelledby="rwa-title">
      <header className="rwa-hero opt-dark">
        <div className="rwa-hero-topline">
          <p className="rwa-eyebrow">Oct-26 Case Study</p>
          <span className="rwa-edition">Market structure / Real-world assets</span>
        </div>
        <div className="rwa-hero-grid">
          <div className="rwa-hero-copy">
            <h1 id="rwa-title">{title}</h1>
            <p className="rwa-deck">{deck}</p>
            <a className="rwa-hero-link" href="#rwa-takeaways">
              Read the key takeaways <span aria-hidden="true">↓</span>
            </a>
          </div>
          <aside className="rwa-decision" aria-label="The management question">
            <p className="rwa-eyebrow">The management question</p>
            <p className="rwa-decision-question">Where can a tokenized product support repeatable, hedgeable liquidity?</p>
            <p className="rwa-decision-note">An institutional trading lens on the claim, the operating model and the conditions for a viable market.</p>
          </aside>
        </div>
        <dl className="rwa-hero-meta">
          <div><dt>Case study as of</dt><dd>{displayDate(asOf)}</dd></div>
          <div><dt>Reading guide</dt><dd>Public evidence · analysis · method</dd></div>
          <div className="rwa-evidence-cutoff"><dt>Evidence cutoff</dt><dd className="rwa-meta-note">{displayDate(evidenceCutoff)}</dd></div>
        </dl>
      </header>

      <section className="rwa-takeaways" id="rwa-takeaways" aria-labelledby="rwa-takeaways-title">
        <div className="rwa-takeaways-topline">
          <h2 id="rwa-takeaways-title">Key takeaways</h2>
          <span className="rwa-kind">Analysis</span>
        </div>
        <div className="rwa-takeaway-grid">
          {keyTakeaways.map((takeaway, index) => (
            <div className="rwa-takeaway" key={takeaway.title}>
              <span className="rwa-takeaway-number" aria-hidden="true">0{index + 1}</span>
              <h3>{takeaway.title}</h3>
              <p>{takeaway.text}</p>
              <SourceLinks sourceIds={takeaway.sourceIds} />
            </div>
          ))}
        </div>
      </section>

      <div className="rwa-reading-layout">
        <aside className="rwa-contents">
          <nav aria-label="Case study contents">
            <p className="rwa-eyebrow">In this case study</p>
            <ol>
              <li><a href="#rwa-market-map">The operating chain</a></li>
              <li><a href="#rwa-product-types">Compare the claims</a></li>
              {sections.map(section => (
                <li key={section.id}><a href={`#rwa-${section.id}`}>{section.title}</a></li>
              ))}
              <li><a href="#rwa-public-evidence">Public evidence</a></li>
            </ol>
          </nav>
          <div className="rwa-reading-key">
            <p className="rwa-eyebrow">How to read</p>
            <p><strong>Public evidence</strong> identifies what the cited material supports.</p>
            <p><strong>Analysis</strong> explains the strategic interpretation.</p>
            <p><strong>Method & scope</strong> states the boundaries of the assessment.</p>
          </div>
        </aside>

        <div className="rwa-reading-body">
          <section className="rwa-section" id="rwa-market-map" aria-labelledby="rwa-map-title">
            <div className="rwa-section-heading">
              <span className="rwa-section-number" aria-hidden="true">01</span>
              <div>
                <p className="rwa-eyebrow">Analytical framework</p>
                <h2 id="rwa-map-title">Follow the operating chain</h2>
                <p>Assess the claim and the market around it, one dependency at a time.</p>
              </div>
            </div>
            <figure className="rwa-flow">
              <ol>
                {FLOW_STEPS.map((step, index) => (
                  <li key={step.title}>
                    <span className="rwa-flow-index" aria-hidden="true">0{index + 1}</span>
                    <h3>{step.title}</h3>
                    <p>{step.question}</p>
                  </li>
                ))}
              </ol>
              <figcaption>Conceptual sequence: asset → issuance → distribution → hedge. These are assessment questions, not measured flows or a claim that every product has a liquid hedge.</figcaption>
            </figure>
          </section>

          <section className="rwa-section" id="rwa-product-types" aria-labelledby="rwa-products-title">
            <div className="rwa-section-heading">
              <span className="rwa-section-number" aria-hidden="true">02</span>
              <div>
                <p className="rwa-eyebrow">Product taxonomy</p>
                <h2 id="rwa-products-title">Compare the claims, then the liquidity</h2>
                <p>Product structure is grounded in public material. Liquidity and risk are analytical considerations; they are not interchangeable across issuers.</p>
              </div>
            </div>
            <p className="rwa-table-hint" id="rwa-table-hint">The comparison scrolls horizontally on smaller screens.</p>
            <div className="rwa-table-scroll" role="region" aria-label="RWA product comparison" aria-describedby="rwa-table-hint" tabIndex="0">
              <table className="rwa-product-table">
                <caption>Product claims, liquidity mechanics and principal risk considerations</caption>
                <thead>
                  <tr>
                    <th scope="col">Product</th>
                    <th scope="col">What the holder owns</th>
                    <th scope="col">Liquidity considerations</th>
                    <th scope="col">Risk to examine</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.name}>
                      <th scope="row">
                        {product.name}
                        <SourceLinks sourceIds={product.sourceIds} />
                      </th>
                      <td>{product.claim}</td>
                      <td>{product.liquidity}</td>
                      <td>{product.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {sections.map((section, index) => (
            <section className="rwa-section rwa-essay-section" id={`rwa-${section.id}`} key={section.id} aria-labelledby={`rwa-${section.id}-title`}>
              <div className="rwa-section-heading">
                <span className="rwa-section-number" aria-hidden="true">{String(index + 3).padStart(2, '0')}</span>
                <div>
                  <span className={`rwa-kind rwa-kind--${section.kind}`}>{KIND_LABELS[section.kind] || 'Analysis'}</span>
                  <h2 id={`rwa-${section.id}-title`}>{section.title}</h2>
                </div>
              </div>
              <div className="rwa-essay-copy">
                {section.paragraphs.map((paragraph, paragraphIndex) => <p key={paragraphIndex}>{paragraph}</p>)}
                <SourceLinks sourceIds={section.sourceIds} />
              </div>
            </section>
          ))}

          <section className="rwa-section rwa-evidence" id="rwa-public-evidence" aria-labelledby="rwa-evidence-title">
            <div className="rwa-section-heading">
              <span className="rwa-section-number" aria-hidden="true">{String(sections.length + 3).padStart(2, '0')}</span>
              <div>
                <p className="rwa-eyebrow">Source register</p>
                <h2 id="rwa-evidence-title">Public evidence</h2>
                <p>Open the original material directly. Each entry identifies the relevant passage and the claim it supports.</p>
              </div>
            </div>
            <ol className="rwa-evidence-list">
              {sources.map((source, index) => (
                <li key={source.id} id={`rwa-source-${source.id}`}>
                  <span className="rwa-evidence-number" aria-hidden="true">[{index + 1}]</span>
                  <div>
                    <p className="rwa-evidence-publisher">{source.publisher}</p>
                    <h3><a href={source.url} target="_blank" rel="noopener noreferrer" aria-label={`${source.title} (opens in a new tab)`}>{source.title}<span aria-hidden="true"> ↗</span></a></h3>
                    <dl>
                      {source.locator && <div><dt>Relevant passage</dt><dd>{source.locator}</dd></div>}
                      {source.supports && <div><dt>Supports</dt><dd>{Array.isArray(source.supports) ? source.supports.join('; ') : source.supports}</dd></div>}
                      <div><dt>Checked</dt><dd className="rwa-date">{displayDate(source.checkedOn)}</dd></div>
                    </dl>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
      <footer className="rwa-footer">
        <p>GPTN <span aria-hidden="true">/</span> Oct-26 Case Study <span aria-hidden="true">/</span> Real-world assets</p>
        <a href="#rwa-title">Back to top <span aria-hidden="true">↑</span></a>
      </footer>
    </article>
  );
}
